import { useContext } from "react";
import {
  FirmaFiltersContext,
  FirmaFiltersEventContext,
} from "../../../../../context/FiltersContext";
import {
  QueryFilter,
  FirmaFilterKeys,
  FirmaRetskredsFilterSetting,
} from "../../../../../interfaces/filters";
import { FirmaDtoFilterInput } from "../../../../../interfaces/schema";
import { f } from "../../../../../util/f";
import { retskredse } from "../../../../../util/retskredse";
import { AccordionSubFilterItem } from "../../../../accordion/Accordion";
import DefaultDropdownItem from "../../../../dropdown/DefaultDropdownItem";
import SuperDropdown from "../../../../dropdown/SuperDropdown";
import AppliedFilterItem from "../../../../filter/AppliedFilterItem";

// helpers
const filterKey: QueryFilter<FirmaFilterKeys>["filterKey"] = "retskreds";

export default function FirmaRetskredsFilter() {
  // context
  const filters = useContext(FirmaFiltersContext);
  const updateFilters = useContext(FirmaFiltersEventContext);

  // // find active settings
  const settings: FirmaRetskredsFilterSetting[] =
    filters.find((f) => f.filterKey === filterKey)?.settings || [];

  // business
  const retskredseSorted = retskredse.sort((a, b) =>
    a.navn.localeCompare(b.navn, "da-DK"),
  );

  // handlers
  function updateFilterHandler(selectedItem: { kode: number; name: string }) {
    // check if selected element already is in settings otherwise add it
    if (!settings?.some((s) => s.id === selectedItem.kode)) {
      settings.push({ id: selectedItem.kode, name: selectedItem.name });
    }

    const filterString = f<FirmaDtoFilterInput>()
      .addFilter(
        "or",
        settings.map((set) => {
          return {
            retskredsNr: {
              eq: set.id,
            },
          };
        }),
      )
      .build();

    const queryFilter: QueryFilter<
      FirmaFilterKeys,
      FirmaRetskredsFilterSetting[]
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: settings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(id: FirmaRetskredsFilterSetting["id"]) {
    const updatedSettings: FirmaRetskredsFilterSetting[] =
      settings.filter((set) => {
        return id !== set.id;
      }) || [];

    const filterString = f<FirmaDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => {
          return {
            retskredsNr: {
              eq: set.id,
            },
          };
        }),
      )
      .build();

    const update = updatedSettings.length > 0;

    if (update) {
      updateFilters({
        event: "update",
        queryFilter: {
          filterKey: filterKey,
          filter: filterString,
          settings: updatedSettings,
        },
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionSubFilterItem
      title="Retskreds"
      value="retskreds"
      isActive={!!settings.length}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings?.map((filter) => (
            <AppliedFilterItem
              key={filter.id}
              label={filter.name}
              removeFilterHandler={() => removeFilterHandler(filter.id)}
            />
          ))}
        </div>
      )}

      <SuperDropdown
        items={retskredseSorted}
        itemsIdKey="navn"
        itemToString={(item) => item?.navn ?? ""}
        itemToElement={(item) => <DefaultDropdownItem label={`${item.navn}`} />}
        filterKeys={["navn", "id"]}
        placeholder="Søg efter navn eller nr ..."
        clearInputAfterSelection={true}
        onSelectedItemEvent={(item) => {
          if (item.selectedItem?.id && item.selectedItem.navn) {
            updateFilterHandler({
              kode: item.selectedItem.id,
              name: item.selectedItem.navn,
            });
          }
        }}
      />
    </AccordionSubFilterItem>
  );
}
